/**
 * Client 360 Records — booking database + document rows for the full-page CRM.
 * Each booking is its own editable record with linked contract/invoice actions.
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Check, Loader2, Send, Plus, FileSignature, Receipt, ChevronDown, Link2, ExternalLink, CalendarDays, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt$ = (n) => `$${Number(n || 0).toLocaleString()}`;

export const copyLink = (path) => {
  const url = `${window.location.origin}${path}`;
  if (navigator.clipboard) {
    navigator.clipboard.writeText(url).then(() => toast.success('Link copied — text or email it to the client'), () => toast.message(url, { duration: 8000 }));
  } else toast.message(url, { duration: 8000 });
};

export const sendDoc = async (kind, id, setBusy, reload) => {
  setBusy(true);
  try {
    const r = await fetch(`${API}/api/command-center/${kind}/${id}/send`, { method: 'POST' });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.detail || 'Send failed');
    if (d.email_sent) toast.success(d.message || 'Sent to client');
    else if (d.sms_sent) toast.warning('Email failed — SMS sent to client with details', { duration: 7000 });
    else toast.error(d.email_failed_reason || d.message || 'Send failed');
    reload && reload();
  } catch (e) { toast.error(e.message); }
  setBusy(false);
};

export const MiniBtn = ({ icon: Icon, label, onClick, testid, tone = 'gray', disabled }) => (
  <button
    onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!disabled) onClick(); }}
    disabled={disabled}
    className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50 ${
      tone === 'red' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-gray-100 hover:bg-red-50 hover:text-red-700 text-gray-600'
    }`}
    data-testid={testid}
  >
    <Icon className="h-3 w-3" /> {label}
  </button>
);

// ─── Editable booking record with linked docs ───
export const BookingRow = ({ b, contracts, invoices, reload }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    event_date: (b.event_date || '').slice(0, 10),
    pickup_time: b.pickup_time || '',
    vehicle: b.vehicle || '',
    hours: b.hours || 0,
    total: b.total || 0,
    deposit: b.deposit || 0,
    pickup_address: b.pickup_address || '',
    dropoff_address: b.dropoff_address || '',
    cc_status: b.cc_status || b.status || 'upcoming',
    notes: b.notes || '',
  });
  const editable = !!b.booking_id;
  const contract = (contracts || []).find(c => c.booking_ref === b.booking_id);
  const invoice = (invoices || []).find(i => i.booking_ref === b.booking_id);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/command-center/confirmed-bookings/${b.booking_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, hours: Number(form.hours) || 0, total: Number(form.total) || 0, deposit: Number(form.deposit) || 0 }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Save failed');
      toast.success('Booking updated');
      setOpen(false); reload();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const createDoc = async (kind) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/command-center/bookings/${b.booking_id}/create-${kind}`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(`${kind === 'contract' ? 'Contract' : 'Invoice'} ${d.contract_id || d.invoice_id} ${d.already_exists ? 'already linked' : 'generated from booking'}`);
      reload();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="bg-white rounded-lg border" data-testid={`drawer-booking-${b.booking_id}`}>
      <button onClick={() => editable && setOpen(o => !o)} className={`w-full p-3 text-left ${editable ? 'hover:bg-gray-50' : 'cursor-default'}`} data-testid={`drawer-booking-toggle-${b.booking_id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-black flex items-center gap-1.5">
              {b.vehicle || 'Vehicle TBD'}
              {editable && <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />}
            </p>
            <p className="text-xs text-gray-500">{(b.event_date || '').slice(0, 10)} · {b.pickup_time || ''} {b.hours ? `· ${b.hours}hr` : ''} {b.booking_id ? <span className="font-mono text-gray-400">· {b.booking_id}</span> : ''}</p>
            {b.pickup_address && <p className="text-[11px] text-gray-500 mt-1 truncate">{b.pickup_address} → {b.dropoff_address}</p>}
            <div className="flex items-center gap-1.5 mt-1">
              {contract && <span className="text-[9px] uppercase tracking-wider bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><FileSignature className="h-2.5 w-2.5" />{contract.status === 'signed' ? 'signed' : 'contract'}</span>}
              {invoice && <span className="text-[9px] uppercase tracking-wider bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><Receipt className="h-2.5 w-2.5" />{invoice.status === 'paid' ? 'paid' : 'invoiced'}</span>}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-base font-black text-black">{fmt$(b.total)}</p>
            <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full ${(b.cc_status || b.status) === 'completed' ? 'bg-green-100 text-green-700' : (b.cc_status || b.status) === 'cancelled' ? 'bg-gray-200 text-gray-600' : 'bg-blue-100 text-blue-700'}`}>{b.cc_status || b.status || 'open'}</span>
          </div>
        </div>
      </button>

      {open && editable && (
        <div className="border-t bg-gray-50 p-3 space-y-2" data-testid={`drawer-booking-editor-${b.booking_id}`}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div><label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Date</label><Input type="date" value={form.event_date} onChange={e => set('event_date', e.target.value)} className="h-8 text-xs bg-white" data-testid={`bk-edit-date-${b.booking_id}`} /></div>
            <div><label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Pickup Time</label><Input type="time" value={form.pickup_time} onChange={e => set('pickup_time', e.target.value)} className="h-8 text-xs bg-white" /></div>
            <div><label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Vehicle</label><Input value={form.vehicle} onChange={e => set('vehicle', e.target.value)} className="h-8 text-xs bg-white" data-testid={`bk-edit-vehicle-${b.booking_id}`} /></div>
            <div><label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Hours</label><Input type="number" value={form.hours} onChange={e => set('hours', e.target.value)} className="h-8 text-xs bg-white" /></div>
            <div><label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Total $</label><Input type="number" value={form.total} onChange={e => set('total', e.target.value)} className="h-8 text-xs bg-white" data-testid={`bk-edit-total-${b.booking_id}`} /></div>
            <div><label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Deposit $</label><Input type="number" value={form.deposit} onChange={e => set('deposit', e.target.value)} className="h-8 text-xs bg-white" /></div>
            <div><label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Status</label>
              <select value={form.cc_status} onChange={e => set('cc_status', e.target.value)} className="w-full border rounded h-8 text-xs px-2 bg-white" data-testid={`bk-edit-status-${b.booking_id}`}>
                {['upcoming', 'completed', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Notes</label><Input value={form.notes} onChange={e => set('notes', e.target.value)} className="h-8 text-xs bg-white" /></div>
            <div className="col-span-2"><label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Pickup</label><Input value={form.pickup_address} onChange={e => set('pickup_address', e.target.value)} className="h-8 text-xs bg-white" /></div>
            <div className="col-span-2"><label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Drop-off</label><Input value={form.dropoff_address} onChange={e => set('dropoff_address', e.target.value)} className="h-8 text-xs bg-white" /></div>
          </div>
          <Button onClick={save} disabled={busy} size="sm" className="bg-red-600 hover:bg-red-700 w-full h-8 text-xs" data-testid={`bk-edit-save-${b.booking_id}`}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-1" /> Save Changes</>}
          </Button>

          <div className="pt-2 border-t space-y-1.5" data-testid={`bk-docs-${b.booking_id}`}>
            <p className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Documents</p>
            <div className="flex items-center gap-1.5 flex-wrap">
              {contract ? (
                <>
                  <span className="text-[10px] font-mono text-gray-500">{contract.contract_id}</span>
                  <MiniBtn icon={busy ? Loader2 : Send} label="Send Contract" tone="red" disabled={busy} onClick={() => sendDoc('contracts', contract.contract_id, setBusy, reload)} testid={`bk-send-contract-${b.booking_id}`} />
                  <MiniBtn icon={Link2} label="Sign Link" onClick={() => copyLink(`/contracts/${contract.contract_id}`)} testid={`bk-sign-link-${b.booking_id}`} />
                  <MiniBtn icon={ExternalLink} label="View" onClick={() => window.open(`/contracts/${contract.contract_id}`, '_blank')} testid={`bk-view-contract-${b.booking_id}`} />
                </>
              ) : (
                <MiniBtn icon={FileSignature} label={busy ? '…' : 'Generate Contract'} disabled={busy} onClick={() => createDoc('contract')} testid={`bk-create-contract-${b.booking_id}`} />
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {invoice ? (
                <>
                  <span className="text-[10px] font-mono text-gray-500">{invoice.invoice_id}</span>
                  <MiniBtn icon={busy ? Loader2 : Send} label="Send Invoice" tone="red" disabled={busy} onClick={() => sendDoc('invoices', invoice.invoice_id, setBusy, reload)} testid={`bk-send-invoice-${b.booking_id}`} />
                  <MiniBtn icon={ExternalLink} label="View" onClick={() => window.open(`/invoices/${invoice.invoice_id}`, '_blank')} testid={`bk-view-invoice-${b.booking_id}`} />
                </>
              ) : (
                <MiniBtn icon={Receipt} label={busy ? '…' : 'Generate Invoice'} disabled={busy} onClick={() => createDoc('invoice')} testid={`bk-create-invoice-${b.booking_id}`} />
              )}
            </div>
            <p className="text-[9px] text-gray-400">Regenerate after edits: send the doc again and the client gets the updated version link.</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Booking Database: full record list + new booking form ───
export const BookingDatabase = ({ data, reload }) => {
  const p = data.profile || {};
  const bookings = data.bookings || [];
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ event_date: '', pickup_time: '', vehicle: '', hours: 3, total: 0, deposit: 0, pickup_address: '', dropoff_address: '' });

  const createBooking = async () => {
    if (!form.event_date) { toast.error('Event date required'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/command-center/confirmed-bookings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: p.name, client_phone: p.phone, client_email: p.email,
          ...form, hours: Number(form.hours) || 0, total: Number(form.total) || 0, deposit: Number(form.deposit) || 0,
          cc_status: 'upcoming',
        }),
      });
      if (!r.ok) throw new Error('Create failed');
      toast.success('Booking created');
      setShowNew(false);
      setForm({ event_date: '', pickup_time: '', vehicle: '', hours: 3, total: 0, deposit: 0, pickup_address: '', dropoff_address: '' });
      reload();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="bg-white border border-gray-200 p-4 mb-4" data-testid="crm-booking-database">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1.5">
          <CalendarDays className="h-3.5 w-3.5 text-red-600" /> Booking Database <span className="text-gray-400">· {bookings.length}</span>
        </p>
        <Button size="sm" onClick={() => setShowNew(s => !s)} className="bg-red-600 hover:bg-red-700 h-7 text-[10px]" data-testid="crm-new-booking-btn">
          {showNew ? <><X className="h-3 w-3 mr-1" />Cancel</> : <><Plus className="h-3 w-3 mr-1" />New Booking</>}
        </Button>
      </div>

      {showNew && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 space-y-2" data-testid="crm-new-booking-form">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Input type="date" value={form.event_date} onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))} className="h-8 text-xs bg-white" data-testid="crm-nb-date" />
            <Input type="time" value={form.pickup_time} onChange={e => setForm(f => ({ ...f, pickup_time: e.target.value }))} className="h-8 text-xs bg-white" />
            <Input placeholder="Vehicle" value={form.vehicle} onChange={e => setForm(f => ({ ...f, vehicle: e.target.value }))} className="h-8 text-xs bg-white" data-testid="crm-nb-vehicle" />
            <Input type="number" placeholder="Hours" value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))} className="h-8 text-xs bg-white" />
            <Input type="number" placeholder="Total $" value={form.total} onChange={e => setForm(f => ({ ...f, total: e.target.value }))} className="h-8 text-xs bg-white" data-testid="crm-nb-total" />
            <Input type="number" placeholder="Deposit $" value={form.deposit} onChange={e => setForm(f => ({ ...f, deposit: e.target.value }))} className="h-8 text-xs bg-white" />
            <Input placeholder="Pickup" value={form.pickup_address} onChange={e => setForm(f => ({ ...f, pickup_address: e.target.value }))} className="h-8 text-xs bg-white" />
            <Input placeholder="Drop-off" value={form.dropoff_address} onChange={e => setForm(f => ({ ...f, dropoff_address: e.target.value }))} className="h-8 text-xs bg-white" />
          </div>
          <Button onClick={createBooking} disabled={saving} size="sm" className="bg-red-600 hover:bg-red-700 w-full h-8 text-xs" data-testid="crm-nb-save">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Create Booking'}
          </Button>
        </div>
      )}

      {bookings.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-6">No bookings yet — every booking becomes its own editable record here.</p>
      ) : (
        <div className="space-y-2">
          {bookings.map((b, i) => (
            <BookingRow key={b.booking_id || i} b={b} contracts={data.contracts || []} invoices={data.invoices || []} reload={reload} />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Documents panel: standalone contracts + invoices with send actions ───
const ContractRow = ({ c, reload }) => {
  const [busy, setBusy] = useState(false);
  return (
    <div className="border border-gray-100 rounded-lg p-2.5" data-testid={`drawer-contract-${c.contract_id}`}>
      <div className="flex items-center justify-between gap-2">
        <a href={`/contracts/${c.contract_id}`} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0 hover:text-red-700">
          <p className="text-xs font-bold truncate">{c.contract_type || 'Service Agreement'}</p>
          <p className="text-[10px] text-gray-500 font-mono">{c.contract_id}{c.booking_ref ? ` · ${c.booking_ref}` : ''}</p>
        </a>
        <div className="text-right flex-shrink-0">
          <p className="text-xs font-bold">{fmt$(c.total_amount)}</p>
          <span className={`text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${c.status === 'signed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{c.status || 'awaiting'}</span>
        </div>
      </div>
      <div className="flex gap-1.5 mt-1.5">
        <MiniBtn icon={busy ? Loader2 : Send} label="Send" tone="red" onClick={() => sendDoc('contracts', c.contract_id, setBusy, reload)} testid={`drawer-send-contract-${c.contract_id}`} />
        <MiniBtn icon={Link2} label="Sign Link" onClick={() => copyLink(`/contracts/${c.contract_id}`)} testid={`drawer-sign-link-${c.contract_id}`} />
      </div>
    </div>
  );
};

const InvoiceRow = ({ i, reload }) => {
  const [busy, setBusy] = useState(false);
  const bal = (i.subtotal || 0) - (i.discount || 0) - (i.deposit_paid || 0);
  return (
    <div className="border border-gray-100 rounded-lg p-2.5" data-testid={`drawer-invoice-${i.invoice_id}`}>
      <div className="flex items-center justify-between gap-2">
        <a href={`/invoices/${i.invoice_id}`} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0 hover:text-red-700">
          <p className="text-xs font-bold truncate">{i.bill_to || 'Invoice'}</p>
          <p className="text-[10px] text-gray-500 font-mono">{i.invoice_id}{i.booking_ref ? ` · ${i.booking_ref}` : ''}</p>
        </a>
        <div className="text-right flex-shrink-0">
          <p className="text-xs font-bold">{fmt$(bal > 0 ? bal : (i.subtotal || 0))}</p>
          <span className={`text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${i.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{i.status === 'paid' ? 'paid' : `${fmt$(bal)} due`}</span>
        </div>
      </div>
      <div className="flex gap-1.5 mt-1.5">
        <MiniBtn icon={busy ? Loader2 : Send} label="Send" tone="red" onClick={() => sendDoc('invoices', i.invoice_id, setBusy, reload)} testid={`drawer-send-invoice-${i.invoice_id}`} />
        <MiniBtn icon={Link2} label="Pay Link" onClick={() => copyLink(`/invoices/${i.invoice_id}`)} testid={`drawer-pay-link-${i.invoice_id}`} />
      </div>
    </div>
  );
};

export const DocumentsPanel = ({ data, reload }) => {
  const contracts = data.contracts || [];
  const invoices = data.invoices || [];
  if (contracts.length === 0 && invoices.length === 0) return null;
  return (
    <div className="bg-white border border-gray-200 p-3" data-testid="crm-documents">
      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Contracts & Invoices</p>
      <div className="space-y-1.5">
        {contracts.map(c => <ContractRow key={c.contract_id} c={c} reload={reload} />)}
        {invoices.map(i => <InvoiceRow key={i.invoice_id} i={i} reload={reload} />)}
      </div>
    </div>
  );
};
